import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// 'all' applies no status filter; 'active'/'archived' map to ClientStatus.
export type ArchiveStatusFilter = 'all' | 'active' | 'archived';
export type SortDirection = 'asc' | 'desc';

export type AdminClientsPageState = {
  filters: {
    nameSearch: string;
    archiveStatus: ArchiveStatusFilter;
  };
  sort: {
    field: 'name';
    direction: SortDirection;
  };
  // Create-Client modal (UC-38). The draft name lives in the React Hook Form
  // instance, not here; the slice holds only the cross-render modal state.
  createClientModalOpen: boolean;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminClientsPageState = {
  filters: {
    nameSearch: '',
    archiveStatus: 'all',
  },
  sort: {
    field: 'name',
    direction: 'asc',
  },
  createClientModalOpen: false,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminClientsPageSlice = createSlice({
  name: 'adminClientsPage',
  initialState,
  reducers: {
    setNameSearch(state, action: PayloadAction<string>) {
      state.filters.nameSearch = action.payload;
    },
    setArchiveStatus(state, action: PayloadAction<ArchiveStatusFilter>) {
      state.filters.archiveStatus = action.payload;
    },
    toggleNameSort(state) {
      state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc';
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },
    openCreateClientModal(state) {
      state.createClientModalOpen = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeCreateClientModal(state) {
      state.createClientModalOpen = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
  },
});

export const {
  setNameSearch,
  setArchiveStatus,
  toggleNameSort,
  clearFilters,
  openCreateClientModal,
  closeCreateClientModal,
  setSubmitInProgress,
  setLastErrorKey,
} = adminClientsPageSlice.actions;

type AppRootState = {
  adminClientsPage: AdminClientsPageState;
};

export const selectAdminClientsPage = (state: AppRootState) =>
  state.adminClientsPage;
